import {
  Building2,
  CalendarClock,
  Check,
  MapPin,
  Users,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  addSiteGroupMembers,
  addTenantGroupMembers,
  ensureSiteGroup,
  ensureTenantGroup,
  getSiteGroup,
  getTenantCometchatSettings,
  getTenantGroup,
  renameSiteGroup,
  renameTenantGroup,
  syncSiteGroup,
  updateSiteGroupSettings,
  updateTenantCometchatSettings,
} from '../../../services/cometchatApi';
import PrimaryButton from '../../ui/PrimaryButton';
import { SearchBar } from '../../ui';
import '../CommunityOnboarding/CommunityOnboarding.css';
import {
  fetchSiteListWithGroupMeta,
  formatCometchatApiError,
  getTenantShiftEnabled,
  siteHasGroup,
} from '../cometchatUtils';
import { getAvailableSupervisors } from '../siteApi';
import CreateGroupStepper from './CreateGroupStepper';
import { getInitials } from './mockChatGroupData';
import './ChatGroupManagement.css';

const LEVELS = {
  ORGANIZATION: 'organization',
  SITE: 'site',
};

const getMemberId = member => member?.user_id;

const getMemberDisplayName = member =>
  member?.display_name || member?.user_name || member?.employee_id || 'Unknown';

const CreateGroup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [level, setLevel] = useState(LEVELS.SITE);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [schedulerBased, setSchedulerBased] = useState(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [orgGroupExists, setOrgGroupExists] = useState(false);

  useEffect(() => {
    const preset = location.state;
    if (!preset) return;

    if (preset.level === LEVELS.ORGANIZATION) {
      setLevel(LEVELS.ORGANIZATION);
    } else {
      setLevel(LEVELS.SITE);
      if (preset.siteId) {
        setSelectedSiteId(preset.siteId);
      }
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const loadInitialData = async () => {
      setSitesLoading(true);
      try {
        const [list, tenantGroup] = await Promise.all([
          fetchSiteListWithGroupMeta(),
          getTenantGroup().catch(() => null),
        ]);
        setSites(
          [...list].sort((a, b) =>
            (a.site_name || '').localeCompare(b.site_name || '')
          )
        );
        setOrgGroupExists(Boolean(tenantGroup?.group_guid));
      } catch (loadError) {
        console.error('Failed to load sites:', loadError);
        setError('Failed to load sites. Please try again.');
      } finally {
        setSitesLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (step !== 3) return;

    const loadEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const supervisors = await getAvailableSupervisors();
        setEmployees(Array.isArray(supervisors) ? supervisors : []);
      } catch (loadError) {
        console.error('Failed to load employees:', loadError);
        setError('Failed to load employees. Please try again.');
      } finally {
        setEmployeesLoading(false);
      }
    };

    loadEmployees();
  }, [step]);

  const availableSites = useMemo(
    () => sites.filter(site => !siteHasGroup(site)),
    [sites]
  );

  const filteredEmployees = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    if (!query) return employees;

    return employees.filter(employee => {
      const name = getMemberDisplayName(employee).toLowerCase();
      const id = String(employee.employee_id || employee.user_id || '').toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [employees, memberSearch]);

  const canProceed = () => {
    if (step === 0) {
      if (level === LEVELS.ORGANIZATION && orgGroupExists) return false;
      if (level === LEVELS.SITE && !selectedSiteId) return false;
      return Boolean(level);
    }
    if (step === 1) return groupName.trim().length > 0;
    if (step === 2) return schedulerBased !== null;
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) return;
    setError(null);
    setStep(prev => prev + 1);
  };

  const toggleMember = employee => {
    const id = getMemberId(employee);
    if (!id) return;

    setSelectedMemberIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleCreateSiteGroup = async () => {
    const existingGroup = await getSiteGroup(selectedSiteId);
    if (existingGroup?.group_guid) {
      throw new Error('This site already has a chat group.');
    }

    await ensureSiteGroup(selectedSiteId);

    const name = groupName.trim();
    if (name) {
      await renameSiteGroup(selectedSiteId, name);
    }

    if (schedulerBased === true) {
      const tenantSettings = await getTenantCometchatSettings();
      if (!getTenantShiftEnabled(tenantSettings)) {
        await updateTenantCometchatSettings(true);
      }
      await updateSiteGroupSettings(selectedSiteId, true);
    }

    if (selectedMemberIds.length > 0) {
      await addSiteGroupMembers(selectedSiteId, selectedMemberIds);
      await syncSiteGroup(selectedSiteId);
    }
  };

  const handleCreateOrgGroup = async () => {
    if (orgGroupExists) {
      throw new Error('Organization chat group already exists.');
    }

    const name = groupName.trim();
    await ensureTenantGroup(name ? { name } : {});

    if (name) {
      await renameTenantGroup(name);
    }

    if (schedulerBased === true) {
      await updateTenantCometchatSettings(true);
    }

    if (selectedMemberIds.length > 0) {
      await addTenantGroupMembers(selectedMemberIds);
    }
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);

    try {
      if (level === LEVELS.SITE) {
        if (!selectedSiteId) {
          throw new Error('Select a site to create a group.');
        }
        await handleCreateSiteGroup();
      } else {
        await handleCreateOrgGroup();
      }

      navigate('/onboarding-site-onboarding/chat', {
        state: { refresh: true },
      });
    } catch (createError) {
      console.error('Failed to create chat group:', createError);
      setError(formatCometchatApiError(createError, { action: 'create' }));
    } finally {
      setSubmitting(false);
    }
  };

  const isMemberStep = step === 3;
  const stepperStep = Math.min(step, 2);

  return (
    <div className="community-onboarding-container">
      <div className="community-onboarding-header">
        <div className="community-onboarding-header-text">
          <h2 className="community-onboarding-title">Create Group</h2>
          <p className="community-onboarding-subtitle">
            {step === 0 && 'Choose organization or site level'}
            {step === 1 && 'Name your chat group'}
            {step === 2 && 'Configure scheduler membership'}
            {isMemberStep && 'Add group members'}
          </p>
        </div>
      </div>

      <CreateGroupStepper currentStep={stepperStep} />

      {error && (
        <p className="chat-group-error" role="alert">
          {error}
        </p>
      )}

      <div className="community-onboarding-scroll">
        {step === 0 && (
          <section className="community-section">
            <h3 className="community-section-title">Select Level</h3>
            <p className="community-section-subtitle">
              Choose the level at which you want to create this group.
            </p>

            <div className="chat-group-option-list">
              <label
                className={`chat-group-option-card ${
                  orgGroupExists ? 'disabled' : ''
                } ${level === LEVELS.ORGANIZATION ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="group-level"
                  className="chat-group-option-radio"
                  checked={level === LEVELS.ORGANIZATION}
                  disabled={orgGroupExists}
                  onChange={() => setLevel(LEVELS.ORGANIZATION)}
                />
                <div className="chat-group-option-icon">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="chat-group-option-content">
                  <p className="chat-group-option-title">Organization</p>
                  <p className="chat-group-option-desc">
                    {orgGroupExists
                      ? 'Organization group already exists.'
                      : 'One private group for the whole tenant.'}
                  </p>
                </div>
              </label>

              <label
                className={`chat-group-option-card ${
                  level === LEVELS.SITE ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="group-level"
                  className="chat-group-option-radio"
                  checked={level === LEVELS.SITE}
                  onChange={() => setLevel(LEVELS.SITE)}
                />
                <div className="chat-group-option-icon">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="chat-group-option-content">
                  <p className="chat-group-option-title">Site</p>
                  <p className="chat-group-option-desc">
                    Create a private CometChat group for a single site.
                  </p>
                </div>
              </label>
            </div>

            {level === LEVELS.SITE && (
              <label className="community-field">
                <span>Select site</span>
                <select
                  value={selectedSiteId}
                  onChange={event => setSelectedSiteId(event.target.value)}
                  className="community-grouping-select"
                  disabled={sitesLoading}
                >
                  <option value="">
                    {sitesLoading ? 'Loading sites...' : 'Choose a site'}
                  </option>
                  {availableSites.map(site => (
                    <option key={site.site_id} value={site.site_id}>
                      {site.site_name}
                    </option>
                  ))}
                </select>
                {!sitesLoading && availableSites.length === 0 && (
                  <p className="community-section-subtitle">
                    All sites already have chat groups, or no sites are available.
                  </p>
                )}
              </label>
            )}
          </section>
        )}

        {step === 1 && (
          <section className="community-section">
            <h3 className="community-section-title">Name the Group</h3>
            <p className="community-section-subtitle">
              Display name for this group. The CometChat group ID is assigned
              automatically by the backend.
            </p>
            <label className="community-field">
              <span>Group Name</span>
              <input
                type="text"
                value={groupName}
                onChange={event => setGroupName(event.target.value)}
                placeholder="e.g. Security Team"
                autoFocus
              />
            </label>
          </section>
        )}

        {step === 2 && (
          <section className="community-section">
            <h3 className="community-section-title">Scheduler Based?</h3>
            <p className="community-section-subtitle">
              Should shift roster workers be added and removed automatically?
            </p>

            <div className="chat-group-option-list">
              <label
                className={`chat-group-option-card ${
                  schedulerBased === true ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="scheduler-based"
                  className="chat-group-option-radio"
                  checked={schedulerBased === true}
                  onChange={() => setSchedulerBased(true)}
                />
                <div className="chat-group-option-icon">
                  <CalendarClock className="w-5 h-5" />
                </div>
                <div className="chat-group-option-content">
                  <p className="chat-group-option-title">Yes</p>
                  <p className="chat-group-option-desc">
                    {level === LEVELS.SITE
                      ? 'Requires tenant and site shift sync both ON.'
                      : 'Enables tenant-wide shift membership for the org group.'}
                  </p>
                </div>
              </label>

              <label
                className={`chat-group-option-card ${
                  schedulerBased === false ? 'selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="scheduler-based"
                  className="chat-group-option-radio"
                  checked={schedulerBased === false}
                  onChange={() => setSchedulerBased(false)}
                />
                <div className="chat-group-option-icon">
                  <Users className="w-5 h-5" />
                </div>
                <div className="chat-group-option-content">
                  <p className="chat-group-option-title">No</p>
                  <p className="chat-group-option-desc">
                    Only manually added members and permanent authorised users
                    are synced.
                  </p>
                </div>
              </label>
            </div>

            <div className="chat-group-info-box">
              <h4>About scheduler-based membership</h4>
              <ul>
                <li>
                  <strong>Site + Yes:</strong> Tenant and site shift flags must
                  both be ON. Shift workers at this site join automatically.
                </li>
                <li>
                  <strong>Organization + Yes:</strong> Tenant shift flag ON;
                  workers on active shifts tenant-wide are included.
                </li>
                <li>
                  <strong>No:</strong> Members are managed via our group/members
                  APIs only — never CometChat REST directly.
                </li>
              </ul>
            </div>
          </section>
        )}

        {isMemberStep && (
          <section className="community-section">
            <div
              className={`chat-group-members-banner ${
                schedulerBased ? 'permanent' : 'manual'
              }`}
            >
              <div className="chat-group-members-banner-icon">
                {schedulerBased ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Users className="w-5 h-5" />
                )}
              </div>
              <div>
                <h4>Group Members</h4>
                <p>
                  Selected users are added through our group members API. The
                  backend syncs CometChat — do not add members in CometChat
                  directly.
                </p>
              </div>
            </div>

            <SearchBar
              placeholder="Search employees..."
              value={memberSearch}
              onChange={event => setMemberSearch(event.target?.value ?? '')}
              iconType="search"
              className="w-full mb-3"
            />

            {employeesLoading ? (
              <p className="community-section-subtitle">Loading employees...</p>
            ) : filteredEmployees.length === 0 ? (
              <p className="community-section-subtitle">No employees found.</p>
            ) : (
              <div className="chat-group-member-list">
                {filteredEmployees.map(employee => {
                  const employeeId = getMemberId(employee);
                  if (!employeeId) return null;

                  const isSelected = selectedMemberIds.includes(employeeId);

                  return (
                    <label
                      key={employeeId}
                      className="chat-group-member-item"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleMember(employee)}
                      />
                      <span className="chat-group-member-avatar">
                        {getInitials(getMemberDisplayName(employee))}
                      </span>
                      <div className="chat-group-member-info">
                        <p className="chat-group-member-name">
                          {getMemberDisplayName(employee)}
                        </p>
                        <p className="chat-group-member-id">
                          {employee.employee_id || employeeId}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>

      <div className="community-onboarding-footer">
        <div className="community-onboarding-footer-actions chat-group-footer-actions">
          {isMemberStep ? (
            <PrimaryButton
              className="chat-group-next-btn"
              onClick={handleCreate}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Group'}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              className="chat-group-next-btn"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateGroup;
